import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to determine if URL is absolute
const getImageUrl = (url) => {
  if (!url) return '';
  // If URL starts with http:// or https://, it's already absolute
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  // Otherwise, prepend backend URL for relative paths
  return `${BACKEND_URL}${url}`;
};

export const CustomerLogos = () => {
  const [logos, setLogos] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingLogo, setEditingLogo] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [formData, setFormData] = useState({
    company_name: '',
    logo_url: null,
    website_url: '',
    display_order: 0,
    is_active: true
  });

  useEffect(() => {
    fetchLogos();
  }, []);

  const fetchLogos = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/admin/customer-logos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLogos(response.data);
    } catch (error) {
      console.error('Failed to fetch logos:', error);
      toast.error('Failed to load customer logos');
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Logo file must be less than 5MB');
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    setUploadingLogo(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/upload/poster`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setFormData(prev => ({ ...prev, logo_url: response.data.url }));
      toast.success('Logo uploaded successfully');
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleOpenDialog = (logo = null) => {
    if (logo) {
      setEditingLogo(logo);
      setFormData({
        company_name: logo.company_name,
        logo_url: logo.logo_url,
        website_url: logo.website_url || '',
        display_order: logo.display_order,
        is_active: logo.is_active
      });
    } else {
      setEditingLogo(null);
      setFormData({
        company_name: '',
        logo_url: null,
        website_url: '',
        display_order: 0,
        is_active: true
      });
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.logo_url) {
      toast.error('Please upload a logo');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const url = editingLogo
        ? `${BACKEND_URL}/api/admin/customer-logos/${editingLogo.id}`
        : `${BACKEND_URL}/api/admin/customer-logos`;

      await axios({
        method: editingLogo ? 'put' : 'post',
        url,
        data: formData,
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`Logo ${editingLogo ? 'updated' : 'created'} successfully`);

      setIsDialogOpen(false);
      fetchLogos();
    } catch (error) {
      console.error('Failed to save logo:', error);
      toast.error('Failed to save logo');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this logo?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/admin/customer-logos/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Logo deleted successfully');

      fetchLogos();
    } catch (error) {
      console.error('Failed to delete logo:', error);
      toast.error('Failed to delete logo');
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Customer Logos</h1>
        <Button onClick={() => handleOpenDialog()} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Add New Logo
        </Button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {logos.map((logo) => (
          <Card key={logo.id} className="hover:shadow-lg transition-shadow">
            <CardHeader className="pb-3">
              <div className="flex justify-between items-start">
                <CardTitle className="text-lg">{logo.company_name}</CardTitle>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleOpenDialog(logo)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(logo.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="bg-gray-50 rounded-lg p-4 flex items-center justify-center h-32">
                  {logo.logo_url ? (
                    <img
                      src={getImageUrl(logo.logo_url)}
                      alt={logo.company_name}
                      className="max-h-full max-w-full object-contain"
                    />
                  ) : (
                    <p className="text-gray-400 text-sm">No logo</p>
                  )}
                </div>
                <div className="space-y-1 text-sm">
                  {logo.website_url && (
                    <a
                      href={logo.website_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline flex items-center gap-1"
                    >
                      <ExternalLink className="h-3 w-3" />
                      Visit Website
                    </a>
                  )}
                  <p className="text-gray-600">Order: {logo.display_order}</p>
                  <p className={logo.is_active ? 'text-green-600' : 'text-red-600'}>
                    {logo.is_active ? 'Active' : 'Inactive'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingLogo ? 'Edit Customer Logo' : 'Add Customer Logo'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-semibold text-gray-700">Company Name *</label>
              <Input
                value={formData.company_name}
                onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                placeholder="Company Name"
                required
              />
            </div>

            <div>
              <label className="text-sm font-semibold text-gray-700">Logo Image *</label>
              <Input
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                disabled={uploadingLogo}
              />
              {uploadingLogo && <p className="text-sm text-blue-600 mt-1">Uploading...</p>}
              {formData.logo_url && (
                <div className="mt-2 bg-gray-50 rounded-lg p-4">
                  <img
                    src={getImageUrl(formData.logo_url)}
                    alt="Logo preview"
                    className="max-h-32 mx-auto object-contain"
                  />
                </div>
              )}
            </div>

            <div>
              <label className="text-sm font-semibold text-gray-700">Website URL</label>
              <Input
                type="url"
                value={formData.website_url}
                onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
                placeholder="https://example.com"
              />
            </div>

            <div>
              <label className="text-sm font-semibold text-gray-700">Display Order</label>
              <Input
                type="number"
                value={formData.display_order}
                onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) })}
                placeholder="0"
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="w-4 h-4 text-blue-600"
              />
              <label htmlFor="is_active" className="text-sm font-semibold text-gray-700">
                Active (Show on website)
              </label>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={uploadingLogo}>
                {editingLogo ? 'Update' : 'Create'} Logo
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
